/*
 * The previous interval's numbers change slowly (for Today, only as the clock moves), but the metrics bar refreshes about once a second while traffic arrives. So the previous interval is fetched
 * once per range and kept for `ttlMs`, with one request in flight at a time however many times it is asked for. A failed fetch is not kept (the next ask tries again) and answers null, which the
 * bar shows as no arrows: never a made-up comparison. Pure but for the loader it is given, so it can be tested with a fake one.
 */
export const createPreviousCache = <T>(options: { ttlMs: number; now?: () => number }) => {
  const now = options.now ?? Date.now;
  const kept = new Map<string, { at: number; value: T }>();
  const inFlight = new Map<string, Promise<T | null>>();

  return {
    get: (key: string, load: () => Promise<T>): Promise<T | null> => {
      const hit = kept.get(key);
      if (hit && now() - hit.at < options.ttlMs) return Promise.resolve(hit.value);
      const waiting = inFlight.get(key);
      if (waiting) return waiting;
      const request = load()
        .then((value) => {
          kept.set(key, { at: now(), value });

          return value;
        })
        .catch(() => null)
        .finally(() => {
          inFlight.delete(key);
        });
      inFlight.set(key, request);

      return request;
    },
    clear: () => {
      kept.clear();
      inFlight.clear();
    },
  };
};
